import { useState, useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { clsx } from 'clsx';
import { useCampaignTimeseries } from '../hooks/useCampaignTimeseries';

type Metric = 'spend' | 'purchases' | 'ctr' | 'cpa';

const METRICS: { value: Metric; label: string }[] = [
  { value: 'spend', label: 'Spend' },
  { value: 'purchases', label: 'Purchases' },
  { value: 'ctr', label: 'CTR' },
  { value: 'cpa', label: 'CPA' },
];

const COLOR: Record<Metric, string> = {
  spend: '#0d9488',
  purchases: '#10b981',
  ctr: '#3b82f6',
  cpa: '#f59e0b',
};

export function CampaignTimeseriesChart({ campaignId }: { campaignId: string }) {
  const [metric, setMetric] = useState<Metric>('spend');
  const { data, loading } = useCampaignTimeseries(campaignId);

  const chartData = useMemo(() => {
    return (data?.points ?? []).map((p) => ({
      date: p.date.slice(5), // MM-DD
      [metric]: p[metric],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, metric]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-navy-900">Daily Trend</h3>
        <div className="flex gap-1" role="tablist">
          {METRICS.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => setMetric(m.value)}
              role="tab"
              aria-selected={metric === m.value}
              className={clsx(
                'rounded-full border px-3 py-1 text-xs',
                metric === m.value
                  ? 'border-teal-500 bg-teal-50 text-teal-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="h-64 rounded bg-slate-100 animate-pulse" />
      ) : chartData.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-sm text-slate-400">
          No daily data available for this date range.
        </div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
              <Tooltip
                contentStyle={{ fontSize: 11 }}
                labelStyle={{ color: '#0f172a' }}
              />
              <Line
                type="monotone"
                dataKey={metric}
                stroke={COLOR[metric]}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
